import { useMutation, useQueryClient } from 'react-query';
import { Badge, Button, Spinner } from 'react-bootstrap';
import { FaCheck, FaExclamationTriangle, FaClock, FaRedo } from 'react-icons/fa';
import { ObjectId } from '../../models';
import { trainModelWithFaq, trainModelWithTextBlock, trainModelWithFile, trainModelWithWebLink} from '../../api/agent';
import { useOrgContext } from '../../context/org';

type Props = {
    id: ObjectId;
    type: "textBlock" | "faq" | "webLink" | "file";
    orgId: ObjectId;
    agentId: string;
    status: string;
}

function TextBlockStatus({orgId, agentId, id, status, type} : Props) {
    
    const queryClient = useQueryClient();
    const { mutateAsync: trainWithTextBlockFn, isLoading } = useMutation(
        trainModelWithTextBlock, // mutation function goes here
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['TextBlocks', agentId]);
            },
            onError: (error) => {
                
            },
        }
    );
    const { mutateAsync: trainWithFaqFn, isLoading: isLoadingFaq} = useMutation(
        trainModelWithFaq, // mutation function goes here
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['Faqs', agentId]);
            },
            onError: (error) => {
                
            },
        }
    );
    const { mutateAsync: trainWithFileFn, isLoading: isLoadingFile} = useMutation(
        trainModelWithFile, // mutation function goes here
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['Files', agentId]);
            },
            onError: (error) => {
                
            },
        }
    );
    const { mutateAsync: trainWithWebLinkFn, isLoading: isLoadingWebLink} = useMutation(
        trainModelWithWebLink, // mutation function goes here
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['WebLinks', agentId]);
            },
            onError: (error) => {
                
            },
        }
    );
    const trainModelFn = type === "textBlock" ? trainWithTextBlockFn :
        type === "faq" ? trainWithFaqFn :
        type === "file" ? trainWithFileFn :
        type === "webLink" ? trainWithWebLinkFn : null;

    const {membership} = useOrgContext();
    const allowed = ["owner", "admin", "editor"]
    const permission = !allowed.includes(membership?.role || "");

    const handleTrain = async (id: ObjectId) => {
        if(!agentId) {
            return;
        }
        if (trainModelFn) {
            trainModelFn({orgId, agentId, id});
            // console.log("Done training.")
        }
    }

    const getStatusBadge = () => {
        switch (status) {
            case "active":
                return (
                    <Badge 
                        className="d-flex align-items-center px-2 py-1"
                        style={{ 
                            backgroundColor: "#198754", 
                            color: "#fff",
                            fontSize: "12px",
                            fontWeight: "500"
                        }}
                    >
                        <FaCheck className="me-1" size={10} />
                        Active
                    </Badge>
                );
            case "processing":
                return (
                    <Badge 
                        className="d-flex align-items-center px-2 py-1"
                        style={{ 
                            backgroundColor: "#ffc107", 
                            color: "#fff",
                            fontSize: "12px",
                            fontWeight: "500"
                        }}
                    >
                        <FaClock className="me-1" size={10} />
                        Processing
                    </Badge>
                );
            case "failed":
                return (
                    <Button
                        variant="link"
                        size="sm"
                        className="p-0 d-flex align-items-center"
                        onClick={() => handleTrain(id)}
                        disabled={permission || isLoading}
                        style={{ 
                            color: "#dc3545",
                            textDecoration: "none",
                            fontSize: "12px"
                        }}
                        title="Training failed - Click to retry"
                    >
                        <FaExclamationTriangle className="me-1" size={10} />
                        Failed
                    </Button>
                );
            default:
                return (
                    <Button
                        variant="link"
                        size="sm"
                        className="p-0 d-flex align-items-center"
                        onClick={() => handleTrain(id)}
                        disabled={permission || isLoading}
                        style={{ 
                            color: "#6c757d",
                            textDecoration: "none",
                            fontSize: "12px"
                        }}
                        title="Click to train this text block"
                    >
                        <FaRedo className="me-1" size={10} />
                        {status || "Pending"}
                    </Button>
                );
        }
    };

    return (
        <div className="d-flex align-items-center">
            {isLoading ? (
                <div className="d-flex align-items-center">
                    <Spinner 
                        size="sm" 
                        className="me-2"
                        style={{ width: "16px", height: "16px" }}
                    />
                    <small style={{ color: "#ccc", fontSize: "12px" }}>
                        Training...
                    </small>
                </div>
            ) : (
                getStatusBadge()
            )}
        </div>
    )
}

export default TextBlockStatus